import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidationErrors} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import {environment} from "../../../environments/environment";

@Component({
    selector: 'app-edit-delivery-modal',
    templateUrl: './edit-delivery-modal.component.html',
    styleUrl: './edit-delivery-modal.component.css'
})
export class EditDeliveryModalComponent {

    editDeliveryForm: FormGroup;
    orgId: number;
    
    // Custom validator for name of rider - check for numbers, special characters, and invalid patterns
    static nameOfRiderValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }
        
        const value = control.value.toString().trim();
        
        // Check for numbers (0-9)
        const hasNumbers = /[0-9]/.test(value);
        if (hasNumbers) {
            return { containsNumbers: true };
        }
        
        // Check for special characters (except apostrophes)
        // Allow only letters (a-z, A-Z), spaces, and apostrophes (')
        const validNamePattern = /^[a-zA-Z\s']+$/;
        if (!validNamePattern.test(value)) {
            return { invalidCharacters: true };
        }
        
        // Check for repetitive characters (3 or more same characters in a row)
        const hasRepetitiveChars = /(.)\1{2,}/.test(value);
        if (hasRepetitiveChars) {
            return { repetitiveCharacters: true };
        }
        
        // Check for only apostrophes or only spaces
        const onlyApostrophes = /^'+$/.test(value);
        const onlySpaces = /^\s+$/.test(value);
        if (onlyApostrophes || onlySpaces) {
            return { invalidPattern: true };
        }
        
        // Check for minimum meaningful characters (at least 2 letters)
        const letterCount = (value.match(/[a-zA-Z]/g) || []).length;
        if (letterCount < 2) {
            return { tooShort: true };
        }
        
        return null;
    }

    // Custom validator for contact of rider using libphonenumber
    static contactOfRiderValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }

        const value = control.value.toString().trim();
        const orgId = Number(localStorage.getItem('orgId'));
        const isRW = orgId === environment.rw_ncdSanofiID || orgId === environment.rw_pdsID;
        const countryCode = isRW ? '+250' : '+254';
        const country = isRW ? 'RW' : 'KE';
        const fullNumber = countryCode + value;

        try {
            if (isValidPhoneNumber(fullNumber, country as any)) {
                return null;
            } else {
                return { invalidPhoneNumber: true };
            }
        } catch (error) {
            return { invalidPhoneNumber: true };
        }
    }
    get delivery_outcomes() {
        const base = [
            {value: 'successful', label: 'Successful'},
            {value: 'not successful', label: 'Not successful'},
            {value: 'postponed', label: 'Postponed'}
        ];
        if (this.orgId === environment.rw_ncdSanofiID || this.orgId === environment.rw_pdsID) {
            base.push({value: 'delivered', label: 'Delivered'});
        }
        return base;
    }
    delivery_times = [
        'Morning', 'Afternoon', 'Evening'
    ];

    medication_returned = [
        'Yes', 'No'
    ]

    constructor(
        public dialogRef: MatDialogRef<EditDeliveryModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { delivery: any },
        private fb: FormBuilder) {
    }

    delivery: any;

    ngOnInit(): void {

        this.orgId = Number(localStorage.getItem('orgId'));

        this.delivery = this.data.delivery;

        const initDate = this.delivery?.actual_delivery_date
            ? new Date(this.delivery.actual_delivery_date)   // if came as 'YYYY-MM-DD'
            : new Date();

        console.log("🚀 delivery input received: ", this.delivery);
        if (!this.delivery.actual_delivery_date) {
            const today = new Date().toISOString().split('T')[0];
            this.delivery.actual_delivery_date = today;
        }

        this.editDeliveryForm = this.fb.group({

            id: [this.delivery?.id || ''],
            facility_name: [this.delivery?.facility_name || ''],
            ccc: [this.delivery?.ccc || ''],
            prefered_delivery_date : [this.delivery?.prefered_delivery_date || '', Validators.required],
            group_name : [this.delivery?.group_name || ''],
            actual_delivery_date: [initDate, Validators.required],
            actual_delivery_location: [this.delivery?.actual_delivery_location || '', Validators.required],
            delivery_outcome: [
                this.delivery?.delivery_outcome || '',
                this.delivery?.ccc ? Validators.required : []
            ],
            delivery_time: [this.delivery?.delivery_time || '', Validators.required],
            medication_returned: [
                this.delivery?.medication_returned || '',
                this.delivery?.ccc ? Validators.required : []
            ],
            name_of_rider: [this.delivery?.name_of_rider || '', [Validators.required, EditDeliveryModalComponent.nameOfRiderValidator]],
            contact_of_rider: [this.delivery?.contact_of_rider || '', [Validators.required, EditDeliveryModalComponent.contactOfRiderValidator]],
            comments: [this.delivery?.comments || ''],
            created_by: [localStorage.getItem('email')],
            clients: this.fb.array([]),
        });

        // Populate group delivery client list if it's a group
        if (this.delivery?.group_name && this.delivery?.ccc_list?.length > 0) {
            this.populateClients(this.delivery.ccc_list);

            // Subscribe to delivery_outcome changes per group client
            this.clients.controls.forEach((clientGroup: FormGroup) => {
                clientGroup.get('delivery_outcome')?.valueChanges.subscribe(value => {
                    if (value === 'successful') {
                        clientGroup.get('medication_returned')?.setValue("No");
                    }
                });
            });
        }

        this.editDeliveryForm.get('delivery_outcome')?.valueChanges.subscribe(value => {
            if (value === 'successful') {
                this.editDeliveryForm.get('medication_returned')?.setValue("No");
            }
        });

    }

    get clients(): FormArray {
        return this.editDeliveryForm.get('clients') as FormArray;
    }

    populateClients(cccList: string[]): void {
        cccList.forEach(ccc => {
            this.clients.push(this.fb.group({
                ccc: [ccc],
                delivery_outcome: ['', Validators.required],
                medication_returned: ['', Validators.required],
                comments: ['']
            }));
        });
    }


    save(): void {
        if (this.editDeliveryForm.valid) {
            const result = {
                ...this.editDeliveryForm.value,
                actual_delivery_date: this.toYMD(this.editDeliveryForm.value.actual_delivery_date)
            };
            this.dialogRef.close(result);
        }
    }

    cancel(): void {
        this.dialogRef.close(); // close without saving
    }

    private toYMD(val: any): string {
        if (!val) return '';
        const d: Date = typeof val === 'string' ? new Date(val) : val;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    // Method to handle contact input - only allow digits
    onContactInput(event: any): void {
        const input = event.target;
        const value = input.value.replace(/\D/g, '');
        const isRW = this.orgId === environment.rw_ncdSanofiID;
        const limitedValue = value.substring(0, isRW ? 9 : 9); // RW: 9 digits after +250, KE: 9 after +254
        input.value = limitedValue;
        this.editDeliveryForm.get('contact_of_rider')?.setValue(limitedValue);
    }

    // Method to handle name input - only allow letters, spaces, and apostrophes
    onNameInput(event: any): void {
        const input = event.target;
        const value = input.value;
        
        // Remove any characters that are not letters, spaces, or apostrophes
        const cleanedValue = value.replace(/[^a-zA-Z\s']/g, '');
        
        // Update the input value and form control
        input.value = cleanedValue;
        this.editDeliveryForm.get('name_of_rider')?.setValue(cleanedValue);
    }

    // Method to get formatted phone number for display
    getFormattedPhoneNumber(): string {
        const contactValue = this.editDeliveryForm.get('contact_of_rider')?.value;
        const isRW = this.orgId === environment.rw_ncdSanofiID;
        const countryCode = isRW ? '+250' : '+254';
        const country = isRW ? 'RW' : 'KE';
        if (contactValue && contactValue.length === 9) {
            try {
                const fullNumber = countryCode + contactValue;
                const phoneNumber = parsePhoneNumber(fullNumber, country as any);
                return phoneNumber.formatNational();
            } catch (error) {
                return `${countryCode} ${contactValue}`;
            }
        }
        return contactValue ? `${countryCode} ${contactValue}` : '';
    }


    protected readonly environment = environment;
}
